import {_} from 'lodash';
import {moment} from 'moment';
import {MAX_CACHE_SIZE, SERVICE_PREFIX} from "../constants";
import {_d, generateHash, getWebServiceUrl} from "../util/misc";
import pubsub from "../pubsub";
import {getServiceDefinition, ServiceNames, TCacheType, TServiceName} from "../constants/ApiServices";
import {nsAmplify} from "../util/nsAmplify";

interface ICacheInstance {
    serviceName: TServiceName,
    duration: Date,
    data: {
        data: any
    }
}

export default class CacheManager {

    private cache: Record<string, ICacheInstance> = {};

    getCache(serviceName: TServiceName, params: any, fullResponse: boolean) {

        const hash = CacheManager.getHash(serviceName, params);
        const def = getServiceDefinition(serviceName);
        const cacheType = def.cache.type;
        let cacheInstance: ICacheInstance | undefined;

        switch (cacheType) {
            case "memory":
                cacheInstance = this.cache[hash];
                break;
            case "persist":
                cacheInstance = nsAmplify.store(hash);
                break;
        }

        // do we have a cache instance?
        if (cacheInstance) {
            const isCacheInstanceOverdue = moment(cacheInstance.duration).diff(moment()) < 0;
            if (isCacheInstanceOverdue) {
                // then we'll simply delete the cache
                switch (cacheType) {
                    case "memory":
                        delete this.cache[hash];
                        break;
                    case "persist":
                        nsAmplify.store(hash, null);
                        break;
                }
                return null;
            }

            return cacheInstance.data;
        }

        return null;

    }

    setCache(serviceName: TServiceName, data: any, params: any) {
        const serviceDefinition = getServiceDefinition(serviceName)
        if (!serviceDefinition.cache?.type) {
            return
        }

        switch (serviceDefinition.cache.type) {
            case "persist": {
                const size = JSON.stringify(nsAmplify.store()).length;
                if (size > MAX_CACHE_SIZE) {
                    ServiceNames.forEach(serviceName => this._removeCache(serviceName, "persist"))
                    _d.log(`Flushed persistent cache (old size was ${size})`);
                }
                break;
            }
            case "memory": {
                const size = JSON.stringify(this.cache).length;
                if (size > MAX_CACHE_SIZE) {
                    ServiceNames.forEach(serviceName => this._removeCache(serviceName, "memory"))
                    _d.log(`Flushed memory cache (old size was ${size})`);
                }
                break;
            }
        }

        const m = moment().add(serviceDefinition.cache.duration, "milliseconds").toDate()
        const cacheInstance: ICacheInstance = {
            serviceName: serviceName,
            duration: m,
            data: {
                data: data
            }
        };

        try {
            const hash = CacheManager.getHash(serviceName, params);
            switch (serviceDefinition.cache.type) {
                case "memory":
                    this.cache[hash] = cacheInstance;
                    break;
                case "persist":
                    nsAmplify.store(hash, cacheInstance);
                    break;
            }
        } catch (err) {

        }
    }

    private static getHash(serviceName: TServiceName, params: any) {
        const obj: any = {};

        // put the keys in alphabetical order
        const keys = _.sortBy(Object.keys(params))
        keys.forEach((key: string) => {
            obj[key] = params[key];
        });

        // generate hash
        return SERVICE_PREFIX + serviceName + generateHash(getWebServiceUrl(serviceName) + JSON.stringify(obj));
    }

    removeCache(serviceName: TServiceName | null = null): void {
        if (serviceName) {
            this._removeCache(serviceName, "persist");
            this._removeCache(serviceName, "memory");
        } else {
            this.removeAllCache();
        }
    }

    private _removeCache(serviceName: TServiceName, sourceType: TCacheType) {
        const source: any = this._getCacheSource(sourceType);
        const searchString = SERVICE_PREFIX + serviceName;
        Object.keys(source).forEach((key: string) => {
            const regex = new RegExp(`^${searchString}-?\\d+$`)
            if (regex.test(key)) {
                this._removeCacheItem(sourceType, key);
                _d.log("cache removed", serviceName);
                pubsub.trigger('cache.removed', serviceName)
            }
        });
    }

    private _removeCacheItem(cacheType: TCacheType, key: string) {
        const cacheInstance: ICacheInstance = cacheType == 'persist' ? nsAmplify.store(key) : this.cache[key];
        switch (cacheType) {
            case "persist":
                nsAmplify.store(key, null);
                break;
            case "memory":
                delete this.cache[key];
                break;
        }
        if (cacheInstance?.serviceName) {
            pubsub.trigger('removed.cache', {serviceName: cacheInstance.serviceName});
        }
    }

    private _getCacheSource(sourceType: TCacheType) {
        switch (sourceType) {
            case "persist":
                return nsAmplify.store();
            case "memory":
                return this.cache;
        }
    }

    isCached(serviceName: TServiceName, params: any, fullResponse: boolean): boolean {
        return !!this.getCache(serviceName, params, fullResponse);
    }

    private removeAllCache(): void {
        ServiceNames.forEach(api => this.removeCache(api))
        _d.log("All cache removed");
    }


}
